import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  IconButton,
  InputBase,
  makeStyles,
  Menu,
  MenuItem,
  Checkbox as MuiCheckBox,
} from '@material-ui/core';
import _, { isArray } from 'lodash';
import moment from 'moment';
import { ehandoverAPI } from '../../../../apis';
import { AutoCompleteInput, Dialog, DragDropUpload } from '../../../../components';
import {
  AddIcon,
  DeleteIcon,
  DropDownIcon,
  FileIcon,
  PhotoIcon,
  PostDayIcon,
  TagIcon,
  CheckListIcon,
} from '../../../../components/Icon';
import { i18n } from '../../../../language';
import Tag from '../CreateNote/tag';
import StyleButton from '../../../../components/StyleButton';
import { WarnAlert } from '../CreateNote';
import { getArrayRandomKey, revokePreviewUrl, onKeyUpEnterOrSpace } from '../../../../utils';
import ShowSelectedFiles from '../ShowSelectedFiles';
import getFileType from '../../../../utils/getFileType';
import IndividualUserDialog from '../IndividualUserDialog';
import useUploadFile, { ACCEPT_FILE_TYPES } from '../../../../utils/hooks/useUploadFile';
import ScheduleSendDialog from '../CreateNote/ScheduleSendDialog';
import CustomDialog from '../../../../components/Dialog';
import { getInitCustomRecurrence, getRoomTypeFromApi, getStaffFromApi } from '../../../../utils/eHandoverUtil';
import { WarpSwitch } from '../../../../components/WarpSwitch';
import { generateScheduleDates } from '../../../../utils/scheduleDateUtil';
import { Checkbox } from './checkbox';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0.125rem 0.625rem 0.063rem 0.625rem',
    marginRight: '0.625rem',
    borderRadius: '0.5rem',
    backgroundColor: theme.palette.status.purple,
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.25rem',
  },
  text: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    letterSpacing: '0',
    color: theme.palette.font.white,
    fontWeight: '500',
    height: '2.313rem',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: '0.938rem',
    color: theme.palette.font.white,
    opacity: '0.6',
  },
  iconBtn: {
    padding: '0.25rem 0.188rem 0.188rem 0.25rem',
    marginLeft: '1rem',
  },
  contentInput: {
    fontFamily: 'NotoSans-Medium',
    borderBottom: 'solid 1px #e4e7eb',
    fontSize: '1rem',
    lineHeight: '1.875rem',
    width: '100%',
    fontWeight: '500',
    letterSpacing: '0',
    color: '#2e4469',
    height: '100%',
    paddingBottom: '1.875rem',
  },
  input: {
    width: '100%',
    padding: '1rem',
    borderRadius: '5px',
    border: 'solid 1px #538fff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  checkBoxBody: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '0.875rem',
  },
  tagsBox: { display: 'flex', gridGap: '0.5rem', flexWrap: 'wrap' },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gridGap: '0.625rem',
  },
  tagIcon: {
    fontSize: '1.375rem',
    marginRight: '0.6rem',
    color: theme.palette.font.sub,
  },
  fileIcon: {
    fontSize: '1.5rem',
    color: '#5f9df6',
  },
  fileBtn: {
    padding: '0.5rem',
    backgroundColor: '#f5faff',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  fileIconDisable: {
    fontSize: '1.5rem',
    color: '#c0c6cb',
    cursor: 'non-allowed',
  },
  fileBtnDisable: {
    padding: '0.5rem',
    backgroundColor: theme.palette.font.white,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#c0c6cb',
    cursor: 'not-allowed',
  },
  inputfile: {
    width: '0.1px',
    height: '0.1px',
    opacity: 0,
    overflow: 'hidden',
    position: 'absolute',
    zIndex: -1,
  },
  foot: {
    padding: '1rem 0',
    borderTop: '0.0625rem solid #ddd',
    borderBottom: '0.0625rem solid #ddd',
    display: 'flex',
    flexWrap: 'wrap',
  },
  img: {
    minWidth: '16.563rem',
    height: '3.125rem',
    borderRadius: '8px',
    backgroundColor: ' #f5faff',
    color: theme.palette.font.main,
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.625rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    marginRight: '0.938rem',
    justifyContent: 'space-between',
    marginTop: '0.875rem',
  },
  file: {
    width: '1rem',
    height: '1.25rem',
    color: theme.palette.primary.main,
  },
  box: {
    width: '1.875rem',
    height: '1.875rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  close: {
    width: '1rem',
    height: '1rem',
    color: theme.palette.primary.main,
  },
  closebox: {
    width: '1.875rem',
    height: '1.875rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 0.938rem',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  btnBox: {
    padding: '0.8rem 1.875rem',
    '& .MuiButton-root': {
      color: theme.palette.font.sub,
      fontFamily: 'NotoSans-Medium',
      fontWeight: '500',
    },
    '& .MuiButton-root.MuiButton-textPrimary': {
      color: theme.palette.primary.main,
    },
  },
  checklistBtn: {
    textTransform: 'inherit',
  },
  button: {
    float: 'right',
    width: '10rem',
    height: '3rem',
    padding: '0.875rem 0',
    backgroundColor: '#28d2ab',
    color: theme.palette.font.white,
    fontSize: '1rem',
    fontFamily: 'NotoSans-Medium',
    lineHeight: '1.25rem',
    fontWeight: '500',
    textTransform: 'capitalize',
    borderRadius: '0.625rem 0 0 0.625rem',
    '&:hover': {
      backgroundColor: '#28d2ab',
    },
  },
  drop: {
    width: '2.5rem',
    backgroundColor: '#28d2ab',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.font.white,
    borderRadius: '0 0.625rem 0.625rem 0',
    borderLeft: '0.0625rem solid #fff',
    height: '3rem',
    '&:hover': {
      backgroundColor: '#28d2ab',
    },
  },
  dropIcon: {
    padding: '0.3rem 0.08rem',
    fontSize: '1.5rem',
  },
  dropDate: {
    marginTop: '3.1rem',
    marginLeft: '-2.8rem',
    borderRadius: '0.625rem !important',
    '& .MuiPaper-root': {
      borderRadius: '0.625rem !important',
      boxShadow: ' 0 0.125rem 0.625rem 0 rgba(0, 0, 0, 0.1)',
    },
  },
  postday: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: theme.palette.font.main,
    fontWeight: '500',
    '&:hover': {
      backgroundColor: theme.palette.font.white,
    },
  },
  postdayIcon: {
    width: '1.501rem',
    height: '1.45rem',
    color: theme.palette.primary.main,
    marginRight: '0.688rem',
  },
  pop: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'none',
    },
    '& .MuiPaper-root': {
      width: '43.75rem',
      backgroundColor: theme.palette.font.white,
      borderRadius: '0.9375rem',
      boxShadow: '0 -0.125rem 1.3125rem rgba(30, 56, 140, 0.18)',
      margin: '0',
    },
  },
  poproot: {
    padding: '2.375rem 1.563rem 1.875rem',
    width: '43.75rem',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2.125rem',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    letterSpacing: '0',
    color: theme.palette.font.main,
    fontWeight: 'bold',
  },
  context: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: theme.palette.font.sub,
    fontWeight: '500',
    marginBottom: '0.625rem',
  },
  datepicker: {
    width: '100%',
    '& .MuiInputBase-root': {
      padding: '0 1rem 0 1.25rem',
      borderRadius: '0.625rem',
      border: '0.0625rem solid #e4e7eb',
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: theme.palette.font.main,
      fontWeight: 'normal',
    },
    '& .MuiPaper-root': {
      borderRadius: '0.9375rem',
    },
  },
  Picon: {
    width: '1.125rem',
    height: '1.25rem',
    color: theme.palette.primary.main,
  },
  cancel: {
    textTransform: 'capitalize',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    backgroundColor: '#eff6ff',
    color: theme.palette.primary.main,
    fontWeight: '500',
    borderRadius: '0.625rem',
    border: '0.12rem solid #538fff',
    width: '12.5rem',
    height: '3rem',
    marginRight: '1.25rem',
  },
  confirm: {
    textTransform: 'capitalize',
    width: '12.5rem',
    height: '3rem',
    borderRadius: '0.5rem',
    backgroundColor: '#4c93ff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    color: theme.palette.font.white,
    fontWeight: '500',
    '&:hover': {
      backgroundColor: '#4c93ff',
    },
    '&:disabled': {
      color: theme.palette.font.white,
    },
  },
  groupBox: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'NotoSans-Medium',
    color: '#2E4469',
    gridGap: '0.857rem',
  },
  inputBox: {
    padding: '0.642rem 0.857rem',
    borderRadius: '0.714rem',
    border: '1px solid #E4E7EB',
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: '1rem',
    cursor: 'pointer',
    width: '27rem',
  },
  groupName: {
    fontSize: '1.142rem',
    fontWeight: 500,
    color: '#2E4469',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  editBtn: {
    fontSize: '1.142rem',
    fontWeight: 500,
    color: '#538FFF',
  },
  notifyBox: {
    paddingTop: '1.2rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gridGap: '1.714rem',
    borderTop: '0.0625rem solid #ddd',
    // justifyContent: 'space-between',
  },
  checkBox: {
    marginLeft: '0rem',
    '& .MuiButtonBase-root': {
      padding: '0',
      paddingRight: '0.5rem',
    },
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1.142rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  delete: {
    fontSize: '0.875rem',
    fontFamily: 'NotoSans-Medium',
    color: theme.palette.font.main,
    fontWeight: '500',
    '&:hover': {
      backgroundColor: theme.palette.font.white,
    },
  },
  delDraftPopContent: {
    fontSize: '1.142rem',
    fontFamily: 'NotoSans-Medium',
    fontWeight: 500,
  },
  acknowledgeBox: {
    margin: '0 0 0.5rem 0',

    '& .MuiFormControlLabel-label': {
      marginLeft: '0.571rem',
      fontFamily: 'NotoSans-Medium',
      color: '#2e4469',
      fontWeight: '500',
      fontSize: '1.142rem',
    },
  },
  dialogCancel: {
    textTransform: 'capitalize',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    backgroundColor: '#eff6ff',
    color: '#538fff',
    fontWeight: '500',
    borderRadius: '0.625rem',
    border: '0.12rem solid #538fff',
    width: '12.5rem',
    height: '3rem',
    marginRight: '1.25rem',
    '&:hover': {
      backgroundColor: '#eff6ff',
    },
    '&:disabled': {
      backgroundColor: '#c0c6cb !important',
      color: '#ffffff !important',
    },
  },
}));

const MyDraftButtons = ({ handleShareNote, scheduleDate }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [scheduleSendDialog, setScheduleSendDialog] = useState(false);
  const shareNote = ({ selectedDate, isDraft, customRecurrence = getInitCustomRecurrence() }) => {
    handleShareNote({ postDay: selectedDate, isDraft, customRecurrence });
  };

  return (
    <>
      <StyleButton
        text={i18n.t('SETTING_UPDATE')}
        type="fill"
        backgroundColor="#28d2ab"
        width="12.5rem"
        onClick={() => shareNote({ isDraft: true })}
        borderRadius="0.625rem"
      />
      <Box display="flex">
        <Button className={classes.button} onClick={shareNote} style={{ backgroundColor: '#538FFF' }}>
          {i18n.t('EHANDOVER_CREATENOTE_SHARE')}
        </Button>
        <IconButton
          className={classes.drop}
          style={{ backgroundColor: '#538FFF' }}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
          }}
        >
          <DropDownIcon className={classes.dropIcon} />
        </IconButton>
      </Box>
      <Menu
        disableScrollLock={true}
        className={classes.dropDate}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        keepMounted
      >
        <MenuItem className={classes.postday} onClick={() => setScheduleSendDialog(true)}>
          <PostDayIcon className={classes.postdayIcon} />
          {i18n.t('EHANDOVER_CREATENOTE_POST')}
        </MenuItem>
      </Menu>

      {scheduleSendDialog && (
        <ScheduleSendDialog
          open={scheduleSendDialog}
          setShowDialog={setScheduleSendDialog}
          handleShare={shareNote}
          setDateDrop={setAnchorEl}
          scheduleDate={scheduleDate}
        />
      )}
    </>
  );
};

const Schedule = ({ update, schedule, customRecurrenceConfig, isJustThisOne, itemData }) => {
  const classes = useStyles();
  const [dateDrop, setDateDrop] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showConflitPop, setShowConflitPop] = useState(false);
  const [updatedScheduleConfig, setUpdatedScheduleConfig] = useState(null);
  // 如果本来就打开了自定义重复并且选择更新整个系列，就不可以再关闭自定义重复这个功能
  const disabledCustomCheckbox = customRecurrenceConfig?.customRecurrence && !isJustThisOne;
  // 如果本来就打开了自定义重复并且只更新当前这一条，就只可以改这一条的发送时间，不能改自定义重复里面的配置
  const customRecurrenceDisabled = customRecurrenceConfig?.customRecurrence && isJustThisOne;

  const handleUpdateNote = ({ selectedDate, customRecurrence = customRecurrenceConfig }) => {
    // 如果只改一条，并且改了发送日期，而且重新选择的发送日期与之前schedule list里面其中一天的日期冲突了就弹窗提示
    if (itemData?.customRecurrence && isJustThisOne) {
      const daysOfWeekList = itemData?.daysOfWeek ? itemData?.daysOfWeek.split(',').map((item) => Number(item)) : [];
      const scheduleDateList = generateScheduleDates(
        itemData?.postDay,
        itemData?.intervalValue,
        itemData?.intervalType,
        new Set(daysOfWeekList),
        itemData?.endDate,
        itemData?.monthRepeatType,
        itemData?.monthDayOfWeek
      );
      const updatedDate = moment(selectedDate).format('YYYY-MM-DD');
      const hasUpdatedDateInScheduleList = _.some(scheduleDateList, (scheduleDate) => {
        return moment(scheduleDate, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD') === updatedDate;
      });
      // 冲突并且有改了发送日期才弹窗
      const isUpdated = moment(itemData?.dateStart).format('YYYY-MM-DD') !== updatedDate;
      if (hasUpdatedDateInScheduleList && isUpdated) {
        setUpdatedScheduleConfig({
          selectedDate,
          customRecurrence,
        });
        setShowConflitPop(true);
        return;
      }
    }

    update({ postDay: selectedDate, customRecurrence });
  };

  const handleConfirmUpdate = () => {
    setShowConflitPop(false);
    setUpdatedScheduleConfig(null);
    update({
      postDay: updatedScheduleConfig?.selectedDate,
      customRecurrence: updatedScheduleConfig?.customRecurrence,
    });
  };

  return (
    <>
      <Box display="flex">
        <Button className={classes.button} onClick={() => handleUpdateNote({ selectedDate: itemData?.dateStart })}>
          {i18n.t('SETTING_UPDATE')}
        </Button>
        <IconButton
          className={classes.drop}
          onClick={(event) => {
            setDateDrop(event.currentTarget);
          }}
        >
          <DropDownIcon className={classes.dropIcon} />
        </IconButton>
      </Box>
      <Menu
        disableScrollLock={true}
        className={classes.dropDate}
        anchorEl={dateDrop}
        open={Boolean(dateDrop)}
        onClose={() => {
          setDateDrop(null);
        }}
        keepMounted
      >
        <MenuItem className={classes.postday} onClick={() => setDialogOpen(true)}>
          <PostDayIcon className={classes.postdayIcon} />
          {i18n.t('EHANDOVER_CREATENOTE_POST')}
        </MenuItem>
      </Menu>

      {dialogOpen && (
        <ScheduleSendDialog
          open={dialogOpen}
          setShowDialog={setDialogOpen}
          handleShare={handleUpdateNote}
          setDateDrop={setDateDrop}
          scheduleDate={schedule}
          initCustomRecurrence={customRecurrenceConfig}
          customRecurrenceDisabled={customRecurrenceDisabled}
          disabledCustomCheckbox={disabledCustomCheckbox}
        />
      )}

      <Dialog
        handleClose={() => setShowConflitPop(false)}
        title={i18n.t('EHANDOVER_WARNING')}
        cancel={i18n.t('EHANDOVER_TASKITEM_CANCEL')}
        confirm={i18n.t('EHANDOVER_TASKITEM_CONFIRM')}
        handleConfirm={handleConfirmUpdate}
        handleCancel={() => setShowConflitPop(false)}
        dialogfootStyle={{ marginTop: '1rem' }}
        open={showConflitPop}
        width="100%"
        dialogWidth="50%"
        dialogContentStyle={{ overflowY: 'auto' }}
      >
        <Box className={classes.delDraftPopContent}>{i18n.t('EHANDOVER_EDIT_JUST_THIS_ONE_WARNING')}</Box>
      </Dialog>
    </>
  );
};

const EditTask = ({
  checklist,
  itemData,
  handleCancelEdit,
  handleSearch,
  type,
  getNewTaskItemListAPI,
  individualUsers,
  noteHeader = null,
  customRecurrenceConfig,
  isJustThisOne,
  isHideCompletedItem = false,
  onHeightChange = () => {}
}) => {
  const classes = useStyles();
  const [taglist, setTaglist] = useState([]);
  const [tagId, setTagId] = useState(null);
  const [formData, setFormData] = useState({
    context: itemData.description ?? '',
    tags: itemData.tags ? _.map(itemData.tags, (item) => ({ ...item, id: item.tagId })) : [],
    checklistData: checklist ? _.map(checklist, (item, index) => ({ ...item, id: index })) : [],
    roomType: [],
    corpList: [],
    addCheckLists: [],
    delCheckItemIds: [],
    postDay: type === 'postDay' ? moment(itemData.dateStart, 'yyyy-MM-DD HH:mm') : moment(),
    filesIds: itemData.ufTaskWebdavFiles ? itemData.ufTaskWebdavFiles : null,
    requiredAcknowLedge: itemData?.mark === 'N',
    notifyList: { roomTypeList: [], staffList: [] },
    notifyBaseOnSendTo: true,
  });
  const [alertOpen, setAlertOpen] = useState(false);
  const [warnFile, setWarnFile] = useState([]);
  const [fileTypeWarn, setFileTypeWarn] = useState([]);
  const [checklistId, setChecklistId] = useState(10000);
  // 这里放的是api给回来的附件
  const [otherFile, setOtherFile] = useState([]);
  const [showIndividualUserDialog, setShowIndividualUserDialog] = useState(false);
  const [acknowledgeWarnOpen, setAcknowledgeWarnOpen] = useState(false);
  const { selectFile, imgList, setImgList, fileList, setFileList } = useUploadFile();

  const [showCheckList, setShowChecklist] = useState(isArray(checklist) && checklist.length > 0);
  const isAnyCompletedItem = useMemo(() => {
    return isArray(checklist) && checklist.length > 0 && _.some(checklist, (item) => item.IS_COMPLETE === 'Y');
  }, [checklist]);

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);


  const individualDisplayName = useMemo(() => {
    const roomTypeDisplayName = _.map(formData?.roomType, (item) => item?.name);
    const staffDisplayName = _.map(formData?.corpList, (item) => item?.staffName);

    if (roomTypeDisplayName.length !== 0 || staffDisplayName.length !== 0) {
      return [...roomTypeDisplayName, ...staffDisplayName].join(',');
    } else {
      return '';
    }
  }, [formData?.roomType, formData?.corpList]);

  // notify
  const [notifyConfig, setNotifyConfig] = useState({
    ITEM_COMPLETE: false,
    CHECKLIST_COMPLETE: false,
  });
  const needNotifyOptions = [
    {
      label: i18n.t('EHANDOVER_ITEM_COMPLETE'),
      type: 'ITEM_COMPLETE',
    },
    {
      label: i18n.t('EHANDOVER_CHECKLIST_COMPLETE'),
      type: 'CHECKLIST_COMPLETE',
    },
  ];
  const [showNotifyListDialog, setShowNotifyListDialog] = useState(false);
  const notifyDisplayName = useMemo(() => {
    const roomTypeDisplayName = _.map(formData?.notifyList?.roomTypeList, (item) => item?.name);
    const staffDisplayName = _.map(formData?.notifyList?.staffList, (item) => item?.staffName);

    if (roomTypeDisplayName.length !== 0 || staffDisplayName.length !== 0) {
      return [...roomTypeDisplayName, ...staffDisplayName].join(',');
    } else {
      return '';
    }
  }, [formData?.notifyList]);
  // 用户上传的附件大于3个的时候禁用require acknowledge
  const disabledAcknowledgement = useMemo(() => {
    const totalAttachments = (imgList?.length ?? 0) + (fileList?.length ?? 0) + (otherFile?.length ?? 0);
    return totalAttachments > 3;
  }, [otherFile, imgList, fileList]);
  const [showResetAckDialog, setShowResetAckDialog] = useState(false);
  const { hasChecklistChanged, hasAttachmentChanged } = useMemo(() => {
    // 这里只判断会影响 reset acknowledge 的内容变更：
    // checklist 比较当前可见的一级 checklist 标题，attachment 比较原附件是否删改，以及是否新增上传附件。
    let originalChecklistTitleList = [];
    if (isArray(checklist) && checklist.length > 0) {
      originalChecklistTitleList = _.map(
        _.filter(checklist, (item) => item.PARENT_ID === 0 && (item?.TITLE ?? '') !== ''),
        (item) => item.TITLE
      );
    }

    let currentChecklistTitleList = [];
    if (showCheckList) {
      const currentTitleList = _.map(
        _.filter(formData.checklistData, (item) => item.PARENT_ID === 0 && (item?.TITLE ?? '') !== ''),
        (item) => item.TITLE
      );
      const addedTitleList = _.map(
        _.filter(formData.addCheckLists, (item) => (item?.TITLE ?? '') !== ''),
        (item) => item.TITLE
      );
      currentChecklistTitleList = [...currentTitleList, ...addedTitleList];
    }

    const originalAttachmentIds = isArray(itemData?.ufTaskWebdavFiles) ? _.map(itemData.ufTaskWebdavFiles, String) : [];
    const currentAttachmentIds = isArray(formData.filesIds) ? _.map(formData.filesIds, String) : [];

    return {
      hasChecklistChanged: !_.isEqual(originalChecklistTitleList, currentChecklistTitleList),
      hasAttachmentChanged:
        !_.isEqual(originalAttachmentIds, currentAttachmentIds) || imgList.length > 0 || fileList.length > 0,
    };
  }, [checklist, showCheckList, formData, itemData?.ufTaskWebdavFiles, imgList, fileList]);

  const debounceOnHeightChange = useMemo( () => _.debounce(() => {
        if(isMounted?.current ?? false){onHeightChange?.();}
      }, 200), []);

  const formHandler = useCallback(
    (key, id) => {
      switch (key) {
        case 'context':
          return (event) => {
            setFormData((oldData) => ({
              ...oldData,
              [key]: event.target.value,
            }));
          };
        case 'checklistData':
          return (event) => {
            setFormData((oldData) => {
              let temp = { ...oldData };
              const checklistIndex = _.findIndex(temp.checklistData, (item) => item.id === id);
              const addChecklistIndex = _.findIndex(temp.addCheckLists, (item) => item.id === id);
              if (checklistIndex > -1) {
                temp.checklistData[checklistIndex].TITLE = event.target.value;
              } else if (addChecklistIndex > -1) {
                temp.addCheckLists[addChecklistIndex].TITLE = event.target.value;
              }
              return temp;
            });
          };
        case 'tags':
          return (value) => {
            if (!value || !_.isNumber(value)) return;
            setTagId(value);
            setFormData((oldData) => {
              let temp = { ...oldData };
              const tag = _.find(taglist, (item) => item.value === value);
              if (_.some(temp.tags, (item) => item.id === tag.id)) {
                return temp;
              } else {
                temp.tags.push({
                  id: tag.id,
                  tagDesc: tag.text,
                });
                return temp;
              }
            });
          };
        default:
          break;
      }
    },
    [taglist,debounceOnHeightChange]
  );

  // get tag list API
  const getTagListAPI = async () => {
    try {
      let list = [];
      const { result } = await ehandoverAPI.getTagList();
      if (Array.isArray(result) && result.length > 0) {
        list = _.map(result, (item) => {
          return {
            text: item.tagDesc,
            value: item.id,
            id: item.id,
          };
        });
      }

      setTaglist(list);
    } catch (error) {
      console.log('-----Get Tag List error----->', error);
    }
  };

  const handleDeleteTag = useCallback(
    (id) => () => {
      setFormData((oldData) => {
        let temp = { ...oldData };

        temp.tags = _.filter(temp.tags, (item) => item.id !== id);

        return temp;
      });
    },
    []
  );

  const handleDeleteOtherFile = useCallback((id) => {
    setFormData((oldData) => ({
      ...oldData,
      filesIds: _.filter(oldData.filesIds, (item) => String(item) !== String(id)),
    }));

    setOtherFile((oldData) => {
      return _.filter(oldData, (item) => item.id !== id);
    });
    onHeightChange?.();
  }, [onHeightChange]);

  const handleAddChecklist = useCallback(() => {
    setFormData((oldData) => ({
      ...oldData,
      addCheckLists: [
        ...oldData.addCheckLists,
        {
          TITLE: '',
          id: checklistId,
        },
      ],
    }));
    setChecklistId((oldId) => oldId + 1);
    onHeightChange?.();
  }, [checklistId, onHeightChange]);

  const handleDeleteChecklist = useCallback(
    (id) => () => {
      setFormData((oldData) => {
        let temp = { ...oldData };
        const checklistIndex = _.findIndex(temp.checklistData, (item) => item.id === id);
        const addChecklistIndex = _.findIndex(temp.addCheckLists, (item) => item.id === id);

        if (checklistIndex > -1) {
          temp.checklistData = _.filter(temp.checklistData, (item) => item.id !== id);
          temp.delCheckItemIds.push(_.find(oldData.checklistData, (item) => item.id === id).ID);
        } else if (addChecklistIndex > -1) {
          temp.addCheckLists = _.filter(temp.addCheckLists, (item) => item.id !== id);
        }

        return temp;
      });
      onHeightChange?.();
    },
    [onHeightChange]
  );

  const list = useMemo(() => {
    let checkList = [];
    let checkSubList = [];

    if (Array.isArray(formData.checklistData) && formData.checklistData.length > 0) {
      checkList = _.filter(formData.checklistData, (item) => {
        return item.PARENT_ID === 0;
      });
      checkSubList = _.filter(formData.checklistData, (item) => {
        return item.PARENT_ID !== 0;
      });
    }

    checkList = checkList.filter(item => isHideCompletedItem ? item.IS_COMPLETE !== 'Y' : true);

    checkList = [...checkList, ...formData.addCheckLists];
    return {
      checkList,
      checkSubList,
    };
  }, [formData.checklistData, formData.addCheckLists, isHideCompletedItem]);

  const getFileDetail = useCallback(async () => {
    try {
      if (!isArray(itemData.ufTaskWebdavFiles)) {
        return;
      }
      const result = await Promise.all(
        itemData.ufTaskWebdavFiles.map((item) =>
          ehandoverAPI.getFileCache(
            {
              attachmentId: item,
            },
            { isHideLoading: true, isHideError: true }
          )
        )
      );

      setOtherFile(
        _.map(result, (item) => ({
          name: item.result.NAME,
          id: item.result.ID,
          previewUrl: getFileType(item.result.NAME) === 'picture' ? item?.result?.DOWNLOAD_URL : '',
        }))
      );
    } catch (error) {
      console.log(error);
    }
  }, [itemData.ufTaskWebdavFiles]);

  useEffect(() => {
    (async () => {
      await getFileDetail();
    })();
  }, [getFileDetail]);

  const getUpdateParams = useCallback(
    ({ postDay = null, isDraft = false, resetAckRecord = false }) => {
      let delCheckItemIds = formData.delCheckItemIds;
      let addCheckLists = (formData.addCheckLists ?? []).filter((addItem) => (addItem?.TITLE ?? '') !== '');
      let checklistData = formData.checklistData;
      if (!showCheckList) {
        delCheckItemIds = isArray(delCheckItemIds) ? delCheckItemIds : [];
        delCheckItemIds = delCheckItemIds.concat(_.map(checklistData, (item) => item.ID));
        addCheckLists = [];
        checklistData = [];
      }
      const checkListCompleteIsChecked = notifyConfig?.CHECKLIST_COMPLETE;
      const itemCompleteIsChecked = notifyConfig?.ITEM_COMPLETE;

      return {
        addCheckLists: _.map(addCheckLists, (item, index) => ({
          isComplete: 'N',
          parentId: 0,
          sortIndex: index,
          title: item.TITLE,
        })),
        addTaskFiles: [
          ..._.map(imgList, (item) => ({
            content: item.base64[0],
            name: item.name,
            taskId: itemData.TASK_ID,
          })),
          ..._.map(fileList, (item) => ({
            content: item.base64[0],
            name: item.name,
            taskId: itemData.TASK_ID,
          })),
        ],
        checkLists: _.map(checklistData, (item, index) => ({
          finishBy: item.TOGGLED_BY,
          finishDate: item.TOGGLED_DATE,
          isComplete: item.IS_COMPLETE,
          parentId: item.PARENT_ID,
          sortIndex: index,
          title: item.TITLE,
          updateCheckItemId: item.ID,
        })),
        delCheckItemIds: delCheckItemIds,
        fields: {
          description: formData.context,
          postDay: postDay ? moment(postDay).format('yyyy-MM-DD HH:mm:00') : moment().format('yyyy-MM-DD HH:mm:ss'),
          tags: _.map(formData.tags, (item) => item.id),
          title: formData.context,
        },
        draft: isDraft,
        filesIds: formData.filesIds,
        roomType: _.map(formData?.roomType, (item) => item?.value),
        corpList: _.map(formData?.corpList, (item) => ({ corpId: item?.corpId, staffName: item?.staffName })),
        taskId: itemData.id,
        checklistComplete: checkListCompleteIsChecked ? 'Y' : 'N',
        itemComplete: itemCompleteIsChecked ? 'Y' : 'N',
        checklistConfig: {
          checklistCorpList: _.map(formData?.notifyList?.staffList, (item) => ({
            corpId: item?.corpId,
            staffName: item?.staffName,
          })),
          checklistRoomTypes: _.map(formData?.notifyList?.roomTypeList, (item) => item?.value),
          isBasedSendTo: formData?.notifyBaseOnSendTo ? 'Y' : 'N',
        },
        mark: formData?.requiredAcknowLedge ? 'N' : null,
        renewAcknowledge: resetAckRecord,
      };
    },
    [formData, imgList, fileList, showCheckList, notifyConfig, itemData]
  );

  const handleUpdate = useCallback(
    async ({ resetAckRecord }) => {
      try {
        let params = getUpdateParams({ resetAckRecord });
        await ehandoverAPI.updateTask(params);

        handleSearch();
        // Call back newNote param HandleSearch it ref to newNotes.HandleSearch()
        handleCancelEdit();
        // call API to update detailNotesList on newNotes
        await getNewTaskItemListAPI([Number(itemData.id)]);
        setShowResetAckDialog(false);
        revokePreviewUrl(imgList);
      } catch (error) {}
    },
    [itemData, imgList, handleSearch, handleCancelEdit, getUpdateParams]
  );

  // 这个方法只用于有schedule send的两个tab（schedule tab和my draft tab)
  const handleScheduleUpdate = useCallback(
    async ({ postDay, isDraft = false, customRecurrence = getInitCustomRecurrence() }) => {
      try {
        let params = getUpdateParams({ postDay, isDraft });
        params = { ...params, ...customRecurrence, daysOfWeek: customRecurrence?.daysOfWeek?.join(',') };
        if (type === 'postDay') {
          params.isJustThisOne = isJustThisOne;
          await ehandoverAPI.updateScheduleTask(params);
        } else {
          await ehandoverAPI.updateMyDraftTask(params);
        }

        handleSearch();
        // Call back newNote param HandleSearch it ref to newNotes.HandleSearch()
        handleCancelEdit();
        // call API to update detailNotesList on newNotes
        await getNewTaskItemListAPI([Number(itemData.id)]);
        setShowResetAckDialog(false);
        revokePreviewUrl(imgList);
      } catch (error) {}
    },
    [itemData, imgList, handleSearch, handleCancelEdit, getUpdateParams]
  );

  const handleOnClickChecklistBtn = useCallback(() => {
    if (!showCheckList && formData.checklistData.length === 0 && formData.addCheckLists.length <= 0) {
      handleAddChecklist();
    }

    setShowChecklist((oldData) => {
      return !oldData;
    });
  }, [showCheckList, formData]);

  const handleConfirmIndividual = ({ selectedRoom, selectedStaff, isNotify }) => {
    if (isNotify) {
      setFormData((prev) => ({
        ...prev,
        notifyList: { roomTypeList: selectedRoom, staffList: selectedStaff },
      }));
      setShowNotifyListDialog(false);
    } else {
      setFormData((prev) => ({
        ...prev,
        roomType: selectedRoom,
        corpList: selectedStaff,
      }));
      if (formData?.notifyBaseOnSendTo) {
        setFormData((prev) => ({
          ...prev,
          notifyList: { roomTypeList: selectedRoom, staffList: selectedStaff },
        }));
      }
      setShowIndividualUserDialog(false);
    }
  };

  const getTaskRecipientList = useCallback(async () => {
    try {
      const { result } = await ehandoverAPI.getTaskRecipients({ taskId: itemData?.id });
      const selectedRoomType = getRoomTypeFromApi(individualUsers, result?.roomTypeList);
      const selectedStaff = getStaffFromApi(individualUsers, result?.corpList);
      setFormData((prev) => ({
        ...prev,
        roomType: selectedRoomType,
        corpList: selectedStaff,
      }));
    } catch (error) {}
  }, []);

  const getCheckListConfig = useCallback(async () => {
    try {
      const { result } = await ehandoverAPI.getCheckListConfig({ taskId: itemData?.id });
      const isBasedSendTo = result?.isBasedSendTo === 'Y' ? true : false;
      const selectedRoomType = getRoomTypeFromApi(individualUsers, result?.checklistRoomTypeList ?? []);
      const selectedStaff = getStaffFromApi(individualUsers, result?.checklistCorpList ?? []);
      setNotifyConfig({
        ITEM_COMPLETE: result?.itemComplete === 'Y',
        CHECKLIST_COMPLETE: result?.checklistComplete === 'Y',
      });
      setFormData((prev) => ({
        ...prev,
        notifyList: {
          roomTypeList: selectedRoomType,
          staffList: selectedStaff,
        },
        notifyBaseOnSendTo: isBasedSendTo,
      }));
    } catch (error) {}
  }, []);

  const handleDragDropUpload = async (files) => {
    await selectFile(files, setWarnFile, setAlertOpen, setFileTypeWarn);
  };

  const updateNotifyOptionStatus = (key, isChecked) => {
    setNotifyConfig((prev) => ({
      ...prev,
      [key]: isChecked,
    }));
  };

  const handleOnClick = async (type) => {
    type === 'notify' ? setShowNotifyListDialog(true) : setShowIndividualUserDialog(true);
  };

  const handleShowResetAckDialog = () => {
    const originalRequireAck = itemData?.mark === 'N';
    const hasDescriptionChanged = itemData?.description !== formData?.context;
    // 满足三个条件才会出reset ack record的popup
    // 1.原来就需要acknowledge  2.修改了description/checklist/attachment  3.修改完内容之后也仍需要acknowledge
    if (
      originalRequireAck &&
      formData?.requiredAcknowLedge &&
      (hasDescriptionChanged || hasChecklistChanged || hasAttachmentChanged)
    ) {
      setShowResetAckDialog(true);
    } else {
      handleUpdate({ resetAckRecord: false });
    }
  };

  const handleAcknowledgeAndAttachments = (nextRequiredAcknowLedge) => {
    const totalAttachments = (imgList?.length ?? 0) + (fileList?.length ?? 0);
    let acknowledgeStatus = nextRequiredAcknowLedge;
    if (nextRequiredAcknowLedge && totalAttachments > 3) {
      acknowledgeStatus = false;
      setAcknowledgeWarnOpen(true);
    }
    setFormData((prev) => ({
      ...prev,
      requiredAcknowLedge: acknowledgeStatus,
    }));
  };

  useEffect(() => {
    if (formData?.requiredAcknowLedge) {
      handleAcknowledgeAndAttachments(true);
    }
  }, [imgList, fileList, otherFile]);

  useEffect(() => {
    onHeightChange?.();
  }, [imgList, 
    fileList, 
    formData.tags, 
    formData.addCheckLists,
    formData.delCheckItemIds,
    showCheckList,
    tagId,
    formData.roomType,
    formData.corpList,
    formData.notifyList,

  ]);

  useEffect(() => {
    
    debounceOnHeightChange();
  }, [formData.context]);

  useEffect(async () => {
    getTaskRecipientList();
    getCheckListConfig();
  }, []);

  return (
    <DragDropUpload onUpload={handleDragDropUpload}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {React.isValidElement(noteHeader) && noteHeader}
        <FormControlLabel
          control={
            <WarpSwitch
              checked={formData?.requiredAcknowLedge}
              onChange={(event) => handleAcknowledgeAndAttachments(event.target.checked)}
              color="primary"
              disabled={disabledAcknowledgement}
            />
          }
          label={i18n.t('EHANDOVER_REQUIRE_ACKNOWLEDGEMENT')}
          className={classes.acknowledgeBox}
        />
      </Box>
      <Box display="flex" flexDirection="column">
        <InputBase
          multiline
          className={classes.contentInput}
          value={formData.context}
          onChange={formHandler('context')}
        />
        {showCheckList &&
            list.checkList
            .map((item, index) => (
            <Box display="flex" marginTop={index > 0 ? '1.313rem' : '1.875rem'} key={item.id}>
              <div className={classes.checkBoxBody}>
                <Checkbox
                  // id={ehandoverIds.checkNote + id + S1}
                  check={item.IS_COMPLETE === 'Y'}
                />
              </div>
              <InputBase
                className={classes.input}
                value={item.TITLE}
                onChange={formHandler('checklistData', item.id)}
                disabled={item.IS_COMPLETE === 'Y'}
                endAdornment={
                  item.IS_COMPLETE === 'Y' ? (
                    false
                  ) : (
                    <ButtonBase onClick={handleDeleteChecklist(item.id)}>
                      <DeleteIcon />
                    </ButtonBase>
                  )
                }
              />
            </Box>
          ))}
        {showCheckList && (
          <Box display="flex" justifyContent="space-between" className={classes.btnBox}>
            <Button
              className={classes.checklistBtn}
              color="primary"
              startIcon={<AddIcon style={{ fontSize: '1.063rem' }} />}
              onClick={handleAddChecklist}
            >
              {i18n.t('EHANDOVER_CREATENOTE_CHECKLIST_ADD')}
            </Button>
          </Box>
        )}

        {showCheckList && (
          <Box className={classes.notifyBox}>
            <Box display="flex" flexDirection="row" alignItems="center" gridGap="0.857rem">
              <Box fontSize="1.142rem" fontWeight="500" fontFamily="NotoSans-Medium">
                {i18n.t('EHANDOVER_NOTIFY')}:
              </Box>
              <Box className={classes.groupBox}>
                <Box className={classes.inputBox} style={{ width: '23rem' }} onClick={() => handleOnClick('notify')}>
                  <Box className={classes.groupName}>
                    {notifyDisplayName ? (
                      notifyDisplayName
                    ) : (
                      <Box color="rgb(125, 134, 169)" fontSize="1rem">
                        {i18n.t('EHANDOVER_SELECT')}
                      </Box>
                    )}
                  </Box>
                  <Box className={classes.editBtn}>{i18n.t('EHANDOVER_EDIT')}</Box>
                </Box>
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" gridGap="0.857rem">
              <Box fontSize="1.142rem" fontWeight="500" fontFamily="NotoSans-Medium">
                {i18n.t('EHANDOVER_WHEN')}
              </Box>
              {_.map(needNotifyOptions, (item, index) => (
                <FormControlLabel
                  key={getArrayRandomKey(index, 'ehandover-notify')}
                  control={<MuiCheckBox />}
                  label={item.label}
                  className={classes.checkBox}
                  checked={notifyConfig[item?.type]}
                  onChange={(event) => updateNotifyOptionStatus(item.type, event?.target?.checked)}
                />
              ))}
            </Box>
          </Box>
        )}

        <Box mt="1.575rem" mb="1.25rem">
          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            alignItems="flex-start"
            gridGap="1rem"
            mb="0.5rem"
          >
            <div className={classes.tags}>
              <TagIcon className={classes.tagIcon} />
              <AutoCompleteInput
                // id={`${ehandoverIds.createNote}4`}
                width="16.563rem"
                height="2.5rem"
                itemList={taglist}
                placeholder={i18n.t('EHANDOVER_CREATENOTE_TAG')}
                value={tagId}
                onOpen={() => {
                  if (taglist.length === 0) {
                    (async () => {
                      await getTagListAPI();
                    })();
                  }
                }}
                onChange={formHandler('tags')}
              />
            </div>
            <Box className={classes.groupBox}>
              <Box fontSize="1.142rem" fontWeight={500} width="max-content">
                {i18n.t('EHANDOVER_SEND_TO')}
              </Box>
              <Box className={classes.inputBox} onClick={() => handleOnClick('sendTo')}>
                <Box className={classes.groupName}>
                  {individualDisplayName ? (
                    individualDisplayName
                  ) : (
                    <Box color="rgb(125, 134, 169)" fontSize="1rem">
                      {i18n.t('NOTIFICATION_FORWARD')}
                    </Box>
                  )}
                </Box>
                <Box className={classes.editBtn}>{i18n.t('EHANDOVER_EDIT')}</Box>
              </Box>
            </Box>
          </Box>
          <Box className={classes.tagsBox}>
            {_.map(formData.tags, (tag) => (
              <Tag
                // id={ehandoverIds.tag_delete + index}
                tag={{ tag: tag.tagDesc }}
                key={`${tag.id}Tag`}
                onRemove={handleDeleteTag(tag.id)}
              />
            ))}
          </Box>
        </Box>

        <ShowSelectedFiles
          photo={imgList}
          files={fileList}
          setPhoto={setImgList}
          setFiles={setFileList}
          otherFile={otherFile}
          handleDeleteOtherFile={handleDeleteOtherFile}
        />
        <Box display="flex" justifyContent="space-between" gridColumnGap="1rem" pt="1.5rem">
          <Box display="flex" gridColumnGap="1.25rem">
            <label
              disabled={isAnyCompletedItem}
              onClick={() => !isAnyCompletedItem && handleOnClickChecklistBtn()}
              onKeyUp={(e) => onKeyUpEnterOrSpace(e, () => {!isAnyCompletedItem && handleOnClickChecklistBtn();})}
              className={isAnyCompletedItem ? classes.fileBtnDisable : classes.fileBtn}
              id={`${itemData.id}_checklist`}
            >
              <CheckListIcon className={isAnyCompletedItem ? classes.fileIconDisable : classes.fileIcon} />
              &nbsp;{i18n.t('EHANDOVER_CREATENOTE_CHECKLIST')} {isAnyCompletedItem}
            </label>
            <label className={classes.fileBtn} htmlFor={`${itemData.id}_photo`}>
              <PhotoIcon className={classes.fileIcon} />
              &nbsp;{i18n.t('EHANDOVER_CREATENOTE_PHOTO')}
            </label>
            <label className={classes.fileBtn} htmlFor={`${itemData.id}_file`}>
              <FileIcon className={classes.fileIcon} />
              &nbsp;{i18n.t('EHANDOVER_CREATENOTE_FILE')}
            </label>
          </Box>
          <Box display="flex" gridColumnGap="1.25rem">
            <StyleButton
              text={i18n.t('EHANDOVER_CREATENOTE_POST_CANCEL')}
              type="fill"
              backgroundColor="#f5faff"
              width="12.5rem"
              color="#538fff"
              onClick={handleCancelEdit}
              borderRadius="0.625rem"
            />
            {/* 其他 Tab都用这个update button, 都用handleUpdate 方法 */}
            {!['postDay', 'myDraft'].includes(type) && (
              <StyleButton
                text={i18n.t('SETTING_UPDATE')}
                type="fill"
                backgroundColor="#28d2ab"
                width="12.5rem"
                onClick={handleShowResetAckDialog}
                borderRadius="0.625rem"
              />
            )}
            {/* Schedule Tab */}
            {type === 'postDay' && (
              <Schedule
                update={handleScheduleUpdate}
                schedule={formData.postDay}
                customRecurrenceConfig={customRecurrenceConfig}
                isJustThisOne={isJustThisOne}
                itemData={itemData}
              />
            )}
            {/* My Draft Tab */}
            {type === 'myDraft' && (
              <MyDraftButtons handleShareNote={handleScheduleUpdate} schedule={formData.postDay} />
            )}
          </Box>
        </Box>
        <input
          type="file"
          name={`${itemData.id}_photo`}
          id={`${itemData.id}_photo`}
          accept=".jpg,.png,.jpeg"
          capture="camera"
          onChange={(event) => {
            (async () => {
              await selectFile(event?.target?.files, setWarnFile, setAlertOpen, setFileTypeWarn);
            })();
          }}
          multiple
          className={classes.inputfile}
          onClick={(event) => {
            event.target.value = '';
          }}
        />
        <input
          type="file"
          name={`${itemData.id}_file`}
          id={`${itemData.id}_file`}
          accept={ACCEPT_FILE_TYPES.join(',')}
          onChange={(event) => {
            (async () => {
              await selectFile(event?.target?.files, setWarnFile, setAlertOpen, setFileTypeWarn);
            })();
          }}
          multiple
          className={classes.inputfile}
          onClick={(event) => {
            event.target.value = '';
          }}
        />
        <WarnAlert
          text={
            fileTypeWarn.length > 0
              ? i18n.t('EHANDOVER_FILE_TYPE_WARN', {
                  text: ACCEPT_FILE_TYPES.map((v) => {
                    return `"${v.replace('.', '')}"`;
                  }).join(','),
                })
              : i18n.t('EHANDOVER_FILE_WARN', { value: warnFile.toString() })
          }
          open={alertOpen}
          onClose={() => {
            setAlertOpen(false);
            setWarnFile([]);
          }}
        />
        <WarnAlert
          open={acknowledgeWarnOpen}
          onClose={() => setAcknowledgeWarnOpen(false)}
          text={i18n.t('EHANDOVER_REQUIRE_ACKNOWLEDGEMENT_LIMIT_WARN')}
        />
        {showIndividualUserDialog && (
          <IndividualUserDialog
            open={showIndividualUserDialog}
            closePop={() => setShowIndividualUserDialog(false)}
            roomTypeList={formData?.roomType}
            staffList={formData?.corpList}
            individualUsers={individualUsers}
            handleConfirm={handleConfirmIndividual}
          />
        )}
        {showNotifyListDialog && (
          <IndividualUserDialog
            open={showNotifyListDialog}
            closePop={() => setShowNotifyListDialog(false)}
            roomTypeList={formData?.notifyList?.roomTypeList}
            staffList={formData?.notifyList?.staffList}
            individualUsers={individualUsers}
            handleConfirm={handleConfirmIndividual}
            notifyParam={{
              notifyBaseOnSendTo: formData?.notifyBaseOnSendTo,
              setNotifyBaseOnSendTo: (isBasedOn) => {
                setFormData((prev) => ({
                  ...prev,
                  notifyBaseOnSendTo: isBasedOn,
                }));
              },
              sendToRoomTypeList: formData?.roomType,
              sendToStaffList: formData?.corpList,
            }}
          />
        )}
        {showResetAckDialog && (
          <CustomDialog
            handleClose={() => setShowResetAckDialog(false)}
            handleCancel={() => handleUpdate({ resetAckRecord: false })}
            title={i18n.t('EHANDOVER_CONFIRMATION')}
            cancel={i18n.t('EHANDOVER_DIALOG_NO')}
            confirm={i18n.t('EHANDOVER_DIALOG_YES')}
            handleConfirm={() => handleUpdate({ resetAckRecord: true })}
            dialogfootStyle={{ marginTop: '1rem' }}
            open={showResetAckDialog}
            width="100%"
            dialogWidth="60%"
            dialogContentStyle={{ overflowY: 'auto' }}
            footerButtonList={[
              <Button
                id="individualDialog_clean_button_id"
                className={classes.dialogCancel}
                onClick={() => setShowResetAckDialog(false)}
                key={getArrayRandomKey(9, 'reset_acknowledge_dialog_btn')}
              >
                {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CANCEL')}
              </Button>,
            ]}
            isCapitalize={false}
          >
            <Box className={classes.delDraftPopContent}>{i18n.t('EHANDOVER_RESET_ACKNOWLEDGEMENT_RECORD')}</Box>
          </CustomDialog>
        )}
      </Box>
    </DragDropUpload>
  );
};

export default EditTask;
